import {
  Box,
  Paper,
  Typography,
  Avatar,
  AvatarGroup,
  Button,
  useTheme,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { tokens } from "../../../theme";
import { LocalShippingOutlined, NavigationOutlined } from "@mui/icons-material";
import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import type { OpsUpcomingStream } from "../types/opsDashboard";
import { useNavigate } from "react-router-dom";

function useStreamTimer(startTime: Date | null) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    if (!startTime) return;
    const tick = () => {
      const diff = Math.floor((startTime.getTime() - Date.now()) / 1000);
      if (diff <= 0) {
        setIsLive(true);
        setSecondsLeft(0);
      } else {
        setIsLive(false);
        setSecondsLeft(diff);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  return { secondsLeft, isLive };
}

function getCountdownParts(seconds: number) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  const pad = (n: number) => n.toString().padStart(2, "0");

  const parts = [
    { value: pad(d), label: "d" },
    { value: pad(h), label: "h" },
    { value: pad(m), label: "m" },
    { value: pad(s), label: "s" },
  ];

  // Drop the days segment entirely when it's zero
  return d > 0 ? parts : parts.slice(1);
}

function initials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

interface ActiveServiceFocusProps {
  stream?: OpsUpcomingStream | null;
}

export default function ActiveServiceFocus({
  stream,
}: ActiveServiceFocusProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const id = stream?.id;

  const streamStartTime = useMemo(() => {
    if (!stream?.date || !stream?.start_time) return null;
    const [h, m] = stream.start_time.split(":").map(Number);
    const d = new Date(`${stream.date}T00:00:00`);
    d.setHours(h, m, 0, 0);
    return d;
  }, [stream?.date, stream?.start_time]);

  const { secondsLeft, isLive } = useStreamTimer(streamStartTime);

  if (!stream) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "24px",
          backgroundColor: colors.primary[900],
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 120,
        }}
      >
        <Typography variant="body2" color={colors.primary[400]}>
          No upcoming streams
        </Typography>
      </Paper>
    );
  }

  const producerName = stream.producer
    ? `${stream.producer.first_name} ${stream.producer.last_name}`.trim()
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "24px",
        backgroundColor: colors.primary[900],
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Avatar
            src={
              stream.deceased_image
                ? `${process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "")}${stream.deceased_image}`
                : undefined
            }
            sx={{
              width: 64,
              height: 64,
              border: `2px solid ${colors.primary[700]}`,
              bgcolor: colors.primary[600],
            }}
          >
            {stream.title.charAt(0)}
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              color={colors.primary[100]}
            >
              {stream.title}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.5,
                color: colors.primary[400],
              }}
            >
              <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" fontWeight={500}>
                {[stream.venue_address, stream.venue_city]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            </Box>

            {producerName && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: 18,
                    height: 18,
                    fontSize: "0.6rem",
                    bgcolor: colors.primary[500],
                  }}
                >
                  {stream.producer
                    ? initials(
                        stream.producer.first_name,
                        stream.producer.last_name,
                      )
                    : ""}
                </Avatar>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color={colors.primary[300]}
                >
                  Producer:{" "}
                  <Box component="span" sx={{ color: colors.primary[700] }}>
                    {producerName}
                  </Box>
                </Typography>
              </Box>
            )}

            {stream.call_time && (
              <Typography
                variant="caption"
                fontWeight={600}
                color={colors.primary[400]}
                sx={{ display: "block", mt: 0.25 }}
              >
                Crew call {stream.call_time}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          {streamStartTime ? (
            <>
              {isLive ? (
                <Typography
                  variant="h3"
                  fontWeight={700}
                  color="error.main"
                  sx={{ fontFamily: "monospace", letterSpacing: "1px" }}
                >
                  LIVE
                </Typography>
              ) : (
                <Box
                  sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                >
                  {getCountdownParts(secondsLeft).map((part, idx, arr) => (
                    <Box
                      key={part.label}
                      sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="h3"
                          fontWeight={700}
                          color="warning.main"
                          sx={{
                            fontFamily: "monospace",
                            letterSpacing: "1px",
                            lineHeight: 1,
                          }}
                        >
                          {part.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={900}
                          color={colors.primary[400]}
                          sx={{ fontSize: "0.85rem", mt: 0.25 }}
                        >
                          {part.label}
                        </Typography>
                      </Box>
                      {idx < arr.length - 1 && (
                        <Typography
                          variant="h3"
                          fontWeight={700}
                          color="warning.main"
                          sx={{ fontFamily: "monospace", pb: "18px" }}
                        >
                          :
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <Typography
              variant="caption"
              fontWeight={600}
              color={colors.primary[400]}
            >
              {stream.date}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <AvatarGroup
          max={5}
          sx={{
            "& .MuiAvatar-root": { width: 28, height: 28, fontSize: "0.7rem" },
          }}
        >
          {stream.crew.map((member) => (
            <Avatar
              key={member.crew_member_id}
              src={member.profile_image ?? undefined}
              sx={{ bgcolor: colors.primary[500] }}
            >
              {initials(member.first_name, member.last_name)}
            </Avatar>
          ))}
        </AvatarGroup>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/stream-management/view/${id}`)}
            sx={{
              bgcolor: colors.primary[400],
              color: "#ffffff",
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              "&:hover": { bgcolor: colors.primary[600] },
            }}
          >
            Open details
          </Button>

          <Button
            variant="outlined"
            startIcon={<LocalShippingOutlined fontSize="small" />}
            sx={{
              bgcolor: `${colors.primary[500]}40`,
              border: "none",
              color: colors.primary[500],
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              "&:hover": { borderColor: "#ff9f1c", bgcolor: "#ff9f1c10" },
            }}
          >
            Dispatch
          </Button>

          <Button
            variant="outlined"
            startIcon={<NavigationOutlined fontSize="small" />}
            sx={{
              bgcolor: colors.primary[800],
              border: "none",
              color: "#3a86ff",
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              "&:hover": { borderColor: "#3a86ff", bgcolor: "#3a86ff10" },
            }}
          >
            Route
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
